<script setup>
import { ref, computed } from 'vue'
import { useRoute } from 'vue-router'
import { useUserPartnerStore } from '@/stores/userPartner'
import { useI18n } from 'vue-i18n'  

const { t } = useI18n();

import {
  Dialog,
  DialogPanel,
  Menu,
  MenuButton,
  MenuItem,
  MenuItems,
  TransitionChild,
  TransitionRoot,
} from '@headlessui/vue'

import {
  Bars3Icon,
  BellIcon,
  XMarkIcon,
  HomeIcon,
  UserGroupIcon,
  UserIcon,
  CreditCardIcon,
  Cog6ToothIcon,
  CurrencyEuroIcon,
} from "@heroicons/vue/24/outline"

import { ChevronDownIcon } from '@heroicons/vue/20/solid'

const userPartnerStore = useUserPartnerStore() // 👈 Store Partner

const route = useRoute()
const sidebarOpen = ref(false)


const navigation = computed(() => [
  {
    name: t("partners.nav.dashboard"),
    ahref: '/partner/dashboard',
    icon: HomeIcon,
  },
  {
    name: t("partners.nav.accounts"),
    ahref: '/partner/accounts',
    icon: UserGroupIcon,
  },
  {
    name: t("partners.nav.users"),
    ahref: '/partner/users',
    icon: UserIcon,
  },
  {
    name: t("partners.nav.plans"),
    ahref: '/partner/plans',
    icon: CreditCardIcon,
  },
  {
    name: t("partners.nav.subscription"),
    ahref: '/partner/subscription',
    icon: CurrencyEuroIcon,
  },
])

const userNavigation = computed(() => [
  { name: t("partners.nav.profile"), href: '/partner/profile' },
  { name: t("partners.nav.logout"), href: '/partner/logout' },
])

// ✅ Calcula las iniciales (ej: “Carlos Pérez” → “CP”)
const initials = computed(() => {
  const name = userPartnerStore.profile?.name || ''
  const surname = userPartnerStore.profile?.surname || ''
  return (name.charAt(0) + surname.charAt(0)).toUpperCase()
})
</script>

<template>
  <div class="min-h-screen bg-white">
    <!-- Mobile sidebar -->
    <TransitionRoot as="template" :show="sidebarOpen">
      <Dialog as="div" class="relative z-50 lg:hidden" @close="sidebarOpen = false">
        <TransitionChild as="template" enter="transition-opacity ease-linear duration-300" enter-from="opacity-0"
          enter-to="opacity-100" leave="transition-opacity ease-linear duration-300" leave-from="opacity-100"
          leave-to="opacity-0">
          <div class="fixed inset-0 bg-gray-900/80" />
        </TransitionChild>

        <div class="fixed inset-0 flex">
          <TransitionChild as="template" enter="transition ease-in-out duration-300 transform"
            enter-from="-translate-x-full" enter-to="translate-x-0"
            leave="transition ease-in-out duration-300 transform" leave-from="translate-x-0"
            leave-to="-translate-x-full">
            <DialogPanel class="relative mr-16 flex w-full max-w-xs flex-1">
              <div class="absolute left-full top-0 flex w-16 justify-center pt-5">
                <button type="button" class="-m-2.5 p-2.5" @click="sidebarOpen = false">
                  <span class="sr-only">Close sidebar</span>
                  <XMarkIcon class="h-6 w-6 text-white" />
                </button>
              </div>

              <!-- Sidebar mobile -->
              <div class="relative flex grow flex-col gap-y-5 overflow-y-auto bg-white px-6 pb-4">
                <div class="flex h-16 shrink-0 items-center justify-center">
                  <img src="../../assets/images/logo/logo-zeeclip-growth-partners.svg" alt="ZeeClip Growth Partners"
                    class="h-8 w-auto" />
                </div>
                <nav class="flex flex-1 flex-col">
                  <ul class="-mx-2 space-y-1">
                    <li v-for="item in navigation" :key="item.name">
                      <router-link :to="item.ahref" class="group flex gap-x-3 rounded-md p-2 text-sm font-semibold"
                        :class="[
                          route.path === item.ahref
                            ? 'bg-blue-50 text-blue-600'
                            : 'text-gray-700 hover:bg-gray-50 hover:text-blue-600'
                        ]" @click="sidebarOpen = false">
                        <component :is="item.icon" class="h-6 w-6 shrink-0"
                          :class="route.path === item.ahref ? 'text-blue-600' : 'text-gray-400 group-hover:text-blue-600'" />
                        {{ item.name }}
                      </router-link>
                    </li>
                  </ul>
                </nav>
              </div>
            </DialogPanel>
          </TransitionChild>
        </div>
      </Dialog>
    </TransitionRoot>

    <!-- Desktop sidebar -->
    <div class="hidden lg:fixed lg:inset-y-0 lg:z-50 lg:flex lg:w-72 lg:flex-col bg-blue-800 border-r border-gray-200">
      <div class="flex grow flex-col gap-y-5 overflow-y-auto px-6 pb-4">
        <div class="flex h-16 shrink-0 items-center justify-center">
          <img src="../../assets/images/logo/logo-zeeclip-partners.svg" alt="ZeeClip Growth Partners"
            class="h-8 w-auto" />
        </div>
        <nav class="flex flex-1 flex-col">
          <ul class="-mx-2 space-y-1">
            <li v-for="item in navigation" :key="item.name">
              <router-link :to="item.ahref" class="group flex gap-x-3 rounded-md p-2 text-sm font-semibold" :class="[
                route.path === item.ahref
                  ? 'bg-blue-50 text-blue-600'
                  : 'text-blue-200 hover:bg-gray-50 hover:text-blue-600'
              ]">
                <component :is="item.icon" class="h-6 w-6 shrink-0"
                  :class="route.path === item.ahref ? 'text-blue-600' : 'text-blue-200 group-hover:text-blue-600'" />
                {{ item.name }}
              </router-link>
            </li>
          </ul>
        </nav>
      </div>
    </div>

    <!-- Main content -->
    <div class="lg:pl-72">
      <div
        class="sticky top-0 z-40 flex h-16 items-center border-b border-gray-200 bg-white px-4 shadow-sm sm:px-6 lg:px-8">
        <button type="button" class="-m-2.5 p-2.5 text-gray-700 hover:text-gray-900 lg:hidden"
          @click="sidebarOpen = true">
          <Bars3Icon class="h-6 w-6" />
        </button>

        <div class="flex flex-1 justify-end">


          <!-- Profile dropdown -->
          <Menu as="div" class="relative ml-4">
            <MenuButton class="flex items-center">
              <div
                class="h-8 w-8 flex items-center justify-center rounded-full bg-blue-600 text-white text-sm font-semibold uppercase select-none">
                {{ initials }}
              </div>
              <ChevronDownIcon class="ml-2 h-5 w-5 text-gray-500" />
            </MenuButton>
            <transition enter-active-class="transition ease-out duration-100"
              enter-from-class="transform opacity-0 scale-95" enter-to-class="transform opacity-100 scale-100"
              leave-active-class="transition ease-in duration-75" leave-from-class="transform opacity-100 scale-100"
              leave-to-class="transform opacity-0 scale-95">
              <MenuItems
                class="absolute right-0 z-10 mt-2 w-32 origin-top-right rounded-md bg-white py-2 shadow-lg ring-1 ring-gray-900/5">
                <MenuItem v-for="item in userNavigation" :key="item.name" v-slot="{ active }">
                <a :href="item.href" :class="[active ? 'bg-white-50' : '', 'block px-3 py-1 text-sm text-gray']">
                  {{ item.name }}
                </a>
                </MenuItem>
              </MenuItems>
            </transition>
          </Menu>
        </div>
      </div>

      <main class="bg-blue-50 min-h-screen">
        <div class="sm:px-6 lg:px-8 py-4 ">
          <router-view />
        </div>
      </main>
    </div>
  </div>
</template>
